import type { HttpClient } from '@/infrastructure/http/HttpClient'
import type { MyInvoiceRepository } from '@/domain/myInvoice/MyInvoiceRepository'
import type { MyInvoice, MyInvoicePage, MyInvoiceStatusCount, MyInvoiceStatus, ProcessingStatus, ExtractedData, ExtractedLine, ExtractedLineTax } from '@/domain/myInvoice/MyInvoice'
import { newLineId } from '@/application/myInvoice/draft'

/**
 * Real "rendition files" pool backend (API branch `06-invoice-pool`, shipped). The unit is the file,
 * carrying a consolidated `status` the API computes plus (when available) its `extraction`. Contract
 * source of truth: `finnecto-api/docs/contracts/invoice-files.md`. Endpoints:
 *  - `GET  /api/users/rendition-files/`        → the user's pool (with and without invoice). `?ids=a,b,c`
 *                                                 filters to those files (batch status poll).
 *  - `PUT  /api/users/rendition-files/{id}/`   → edit the extraction; ONLY for files WITHOUT an invoice
 *                                                 (backend 409/400 otherwise). Covers "corregir extracción"
 *                                                 and "Ingresar manualmente" a FAILED file.
 *  - `POST /api/users/rendition-files/{id}/retry/` → re-run the AI extraction on a FAILED file.
 *  - `DELETE /api/users/twilio/files/{filename}/` → discard a file (pre-existing route, deletes by filename).
 * Amounts are raw integers; the front formats them. Wired in index.ts.
 */

/** Raw per-line tax as returned by the extraction API (snake_case). */
interface RawExtractedLineTax {
  tax_name?: string
  percentage?: number
  taxable_amount?: number
  tax_amount?: number
}

/** Raw line as returned by the extraction API (snake_case). */
interface RawExtractedLine {
  id?: number | string
  description?: string
  quantity?: number
  unit_price?: number
  total?: number
  tax_details?: RawExtractedLineTax[]
  is_adjustment?: boolean
}

/** Raw document record (snake_case). */
interface RawMyInvoice {
  id: number | string
  /** Linked invoice id (null while the file has no invoice yet). */
  invoice_id?: number | string | null
  /** Request the linked invoice belongs to (null until associated). */
  request_id?: number | string | null
  file_name?: string
  file_url?: string
  file_type?: string
  status?: string
  /** Raw extraction lifecycle: PENDING | PROCESSING | DONE | FAILED (drives polling). */
  processing_status?: string
  source?: string | null
  uploaded_at?: string
  error_message?: string
  extraction?: {
    provider_name?: string
    provider_tax_id?: string
    invoice_number?: string
    document_type?: string | null
    emission_date?: string
    currency?: string
    net?: number
    tax?: number
    total?: number
    lines?: RawExtractedLine[]
  } | null
}

// Consolidated status the API returns (nombres exactos a afinar cuando aterrice 06-invoice-pool).
const STATUS_MAP: Record<string, MyInvoiceStatus> = {
  PROCESSING: 'PROCESSING',
  FAILED: 'FAILED',
  NOT_SENT: 'NOT_SENT',
  IN_REVIEW: 'IN_REVIEW',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  PAID: 'PAID',
}

const PROCESSING_STATUSES: readonly string[] = ['PENDING', 'PROCESSING', 'DONE', 'FAILED']
function mapProcessingStatus(raw?: string): ProcessingStatus | undefined {
  return raw && PROCESSING_STATUSES.includes(raw) ? (raw as ProcessingStatus) : undefined
}

const SOURCES: readonly string[] = ['WHATSAPP', 'MANUAL_UPLOAD']
function mapSource(raw?: string | null): MyInvoice['source'] {
  return raw && SOURCES.includes(raw) ? (raw as NonNullable<MyInvoice['source']>) : undefined
}

function mapLineTax(raw: RawExtractedLineTax): ExtractedLineTax {
  return {
    taxName: raw.tax_name ?? '',
    percentage: raw.percentage ?? 0,
    taxableAmount: raw.taxable_amount ?? 0,
    taxAmount: raw.tax_amount ?? 0,
  }
}

function mapLine(raw: RawExtractedLine): ExtractedLine {
  return {
    id: raw.id != null ? String(raw.id) : newLineId(),
    description: raw.description ?? '',
    quantity: raw.quantity ?? 0,
    unitPrice: raw.unit_price ?? 0,
    total: raw.total ?? 0,
    taxDetails: (raw.tax_details ?? []).map(mapLineTax),
    ...(raw.is_adjustment ? { isAdjustment: true } : {}),
  }
}

function mapExtraction(raw: NonNullable<RawMyInvoice['extraction']>): ExtractedData {
  return {
    providerName: raw.provider_name ?? '',
    providerTaxId: raw.provider_tax_id,
    invoiceNumber: raw.invoice_number ?? '',
    invoiceType: raw.document_type ?? undefined,
    emissionDate: raw.emission_date ?? '',
    currency: raw.currency ?? 'CLP',
    net: raw.net ?? 0,
    tax: raw.tax ?? 0,
    total: raw.total ?? 0,
    lines: (raw.lines ?? []).map(mapLine),
  }
}

// Normalize whatever the list endpoint returns into an array of raw records. Accepts a bare array or
// a DRF-paginated `{ results: [...] }`; returns [] for any other shape (HTML error page, redirect,
// null) so callers never blow up calling `.map` on a non-array.
function toRawList(data: unknown): RawMyInvoice[] {
  if (Array.isArray(data)) return data as RawMyInvoice[]
  const results = (data as { results?: unknown } | null)?.results
  if (Array.isArray(results)) return results as RawMyInvoice[]
  return []
}

interface RawStatusCount {
  status?: string
  count?: number
}

function mapStatusCount(raw: RawStatusCount): MyInvoiceStatusCount | null {
  const status = STATUS_MAP[raw.status ?? '']
  return status ? { status, count: raw.count ?? 0 } : null
}

// Parse the DRF-paginated envelope (`{ count, results, statuses }`). Tolerates a bare array (older
// shape / error fallback): total falls back to the item count and statuses to [].
function toPage(data: unknown): MyInvoicePage {
  const items = toRawList(data).map(mapInvoice)
  const envelope = (data as { count?: number; statuses?: unknown } | null) ?? {}
  const total = typeof envelope.count === 'number' ? envelope.count : items.length
  const rawStatuses = Array.isArray(envelope.statuses) ? (envelope.statuses as RawStatusCount[]) : []
  const statuses = rawStatuses
    .map(mapStatusCount)
    .filter((s): s is MyInvoiceStatusCount => s !== null)
  return { items, total, statuses }
}

function mapInvoice(raw: RawMyInvoice): MyInvoice {
  return {
    id: String(raw.id),
    invoiceId: raw.invoice_id != null ? String(raw.invoice_id) : undefined,
    requestId: raw.request_id != null ? String(raw.request_id) : undefined,
    fileName: raw.file_name ?? '',
    fileUrl: raw.file_url,
    fileType: raw.file_type,
    status: STATUS_MAP[raw.status ?? ''] ?? 'PROCESSING',
    processingStatus: mapProcessingStatus(raw.processing_status),
    source: mapSource(raw.source),
    uploadedAt: raw.uploaded_at ?? '',
    errorMessage: raw.error_message,
    extracted: raw.extraction ? mapExtraction(raw.extraction) : undefined,
  }
}

function extractionPayload(data: ExtractedData): object {
  return {
    provider_name: data.providerName,
    // `?? ''` is load-bearing: the API declares provider_tax_id as required-but-blankable
    // (`CharField(allow_blank=True)`), and an undefined value is dropped by JSON serialization —
    // which would 400 with "This field is required" on any file whose OCR found no tax id.
    provider_tax_id: data.providerTaxId ?? '',
    invoice_number: data.invoiceNumber,
    emission_date: data.emissionDate,
    currency: data.currency,
    net: data.net,
    tax: data.tax,
    total: data.total,
    lines: data.lines.map((l) => ({
      description: l.description,
      quantity: l.quantity,
      unit_price: l.unitPrice,
      total: l.total,
    })),
  }
}

/** Empty extraction used to seed a FAILED file for manual entry (see `enterManually`). */
function blankExtraction(): ExtractedData {
  return { providerName: '', invoiceNumber: '', emissionDate: '', currency: 'CLP', net: 0, tax: 0, total: 0, lines: [] }
}

export class HttpMyInvoiceRepository implements MyInvoiceRepository {
  constructor(private http: HttpClient) {}

  async list(params?: {
    selectable?: boolean
    status?: MyInvoiceStatus
    page?: number
    pageSize?: number
    detail?: 'summary' | 'full'
  }): Promise<MyInvoicePage> {
    const query: Record<string, string> = {}
    // Drawer-only: only files that can still enter a rendition. Django's BooleanFilter expects "True".
    if (params?.selectable) query.selectable = 'True'
    if (params?.status) query.status = params.status
    if (params?.page != null) query.page = String(params.page)
    if (params?.pageSize != null) query.page_size = String(params.pageSize)
    if (params?.detail) query.detail = params.detail
    const req = this.http.url('users/rendition-files')
    if (Object.keys(query).length) req.params(query)
    const data = await req.get<unknown>()
    // `toPage` is defensive about the response shape: a DRF-paginated envelope, a bare array, or —
    // on an error/redirect — an HTML page, which yields an empty page so the UI shows its clean
    // empty state instead of a raw ".map is not a function".
    return toPage(data)
  }

  async upload(file: File): Promise<MyInvoice> {
    const fd = new FormData()
    fd.append('file', file)
    // Existing document-upload endpoint (API #2104): drops the file on S3 → async extraction.
    const data = await this.http.url('users/rendition-files/upload').body(fd).post<RawMyInvoice>()
    return mapInvoice(data)
  }

  async listByIds(ids: string[]): Promise<MyInvoice[]> {
    // Batch status poll: one request for all in-flight files via the list endpoint's `?ids=` filter
    // (comma-joined), replacing N parallel `?id=` polls. The backend narrows to these ids (scoped to the
    // user; unknown/foreign ids omitted), so we just map whatever comes back and reconcile by id upstream.
    if (!ids.length) return []
    // `page_size=-1` disables pagination so every polled id comes back in one envelope, not just page 1.
    const data = await this.http
      .url('users/rendition-files')
      .params({ ids: ids.join(','), page_size: '-1' })
      .get<unknown>()
    return toRawList(data).map(mapInvoice)
  }

  async saveReview(id: string, extracted: ExtractedData): Promise<MyInvoice> {
    // Edit the extraction of a pool file that has NO invoice yet. The backend rejects (409/400) once the
    // file has an Invoice — those amounts are edited from the rendition, not here.
    const data = await this.http
      .url(`users/rendition-files/${id}`)
      .body(extractionPayload(extracted))
      .put<RawMyInvoice>()
    return mapInvoice(data)
  }

  async enterManually(id: string): Promise<MyInvoice> {
    // "Ingresar manualmente" a FAILED file = seed a blank extraction via the same edit endpoint; the
    // user then fills it in the pool carousel and saves (another PUT via saveReview).
    const data = await this.http
      .url(`users/rendition-files/${id}`)
      .body(extractionPayload(blankExtraction()))
      .put<RawMyInvoice>()
    return mapInvoice(data)
  }

  async retry(id: string): Promise<MyInvoice> {
    // Re-queue the AI extraction for a FAILED pool file. The backend resets it to PENDING and
    // re-dispatches the extraction task, returning the file already in PROCESSING for polling.
    const data = await this.http.url(`users/rendition-files/${id}/retry`).post<RawMyInvoice>()
    return mapInvoice(data)
  }

  async discard(fileName: string): Promise<void> {
    // Pre-existing route (`users-twilio-file-delete`) deletes by filename, not id.
    await this.http.url(`users/twilio/files/${fileName}`).delete()
  }
}
